import { createClient, Session, User } from '@supabase/supabase-js'
import { emitter } from './emitter'

const SUPABASE_URL = 'https://xeclrrhsnzwxjvjnaive.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhlY2xycmhzbnp3eGp2am5haXZlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzA4NDk5MjEsImV4cCI6MjA4NjQyNTkyMX0.30SzziQh-vCCM9g6IwwmLMJdooashYo85bEJXY1CVio'

// Important: Configure the client with proper auth settings
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
	auth: {
		autoRefreshToken: true,
		persistSession: true,
		detectSessionInUrl: true,
		storage: window.localStorage,
		storageKey: 'sb-xeclrrhsnzwxjvjnaive-auth-token',
		flowType: 'pkce' // Use PKCE flow for better security
	}
})

// Capacitor (native) access via the global the runtime injects — matches how the
// rest of the app reaches plugins (StatusBar etc.), so nothing extra is bundled.
const cap = () => (window as any).Capacitor
const isNative = () => !!cap()?.isNativePlatform?.()
const isIOS = () => cap()?.getPlatform?.() === 'ios'
// Custom scheme the native app claims (see AndroidManifest). Supabase redirects
// here after Google auth so login finishes in the app instead of opening the PWA.
const OAUTH_REDIRECT = 'dev.codesuma.papalist://login-callback'

// Google Web Client ID (the OAuth "Web application" client from Google Cloud — the
// SAME one configured in Supabase's Google provider). Required for ANDROID native
// sign-in: the system account sheet returns an id token whose audience must be this
// id. Until it's filled in, Android falls back to the browser OAuth flow below.
const GOOGLE_WEB_CLIENT_ID = '354990406576-39vc701ba7sa5rll0kh2i2h2eigv8fdt.apps.googleusercontent.com'

// iOS OAuth client (Google Cloud "iOS" type, bundle dev.codesuma.papalist). The
// native Google sheet on iOS needs this + its reversed-id URL scheme registered
// in Info.plist, and the id MUST also be listed in the Supabase Google
// provider's Client IDs — on iOS the id token's audience is THIS id, not the
// web client's.
const GOOGLE_IOS_CLIENT_ID = '354990406576-o0te350fal4nuuaqhbcqj8va2m9ccgfg.apps.googleusercontent.com'

interface AuthState {
	token: string | null
	user: User | null
	session: Session | null
	initialized: boolean
}

let state: AuthState = {
	token: null,
	user: null,
	session: null,
	initialized: false
}

// In-flight anonymous sign-in, so two concurrent first writes don't each mint a user.
let ensuring: Promise<Session | null> | null = null

const subscribers = new Set<(state: any) => void>()

function notifySubscribers() {
	const publicState = {
		token: state.token,
		user: state.user,
		isAuthenticated: !!state.token,
		initialized: state.initialized
	}
	subscribers.forEach((callback) => callback(publicState))
}

function updateState(session: Session | null) {
	state.session = session
	state.token = session?.access_token ?? null
	state.user = session?.user ?? null
	notifySubscribers()
}

// --- Native Google sign-in (system account sheet → id token → Supabase) ---
// Supabase expects the provider to hash the nonce (SHA-256, hex): Google gets the
// hashed nonce, Supabase gets the raw one and compares.
const sha256Hex = async (msg: string): Promise<string> => {
	const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(msg))
	return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}
const randomHex = (): string => {
	const a = new Uint8Array(32)
	crypto.getRandomValues(a)
	return Array.from(a, b => b.toString(16).padStart(2, '0')).join('')
}

// After signing in to a permanent account, hand the previous anonymous session's
// token to the server so it can migrate that user's lists/items to this account.
async function claimAnonymousData(accessToken: string, anonToken: string) {
	try {
		const origin = isNative() ? 'https://papalist.app' : ''
		await fetch(origin + '/api/v1/auth/claim', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
			body: JSON.stringify({ anonToken }),
		})
	} catch (e) {
		console.warn('Anonymous data migration failed (will keep the new account):', e)
	}
}

async function signInWithGoogleNative() {
	// Capture the current anonymous session BEFORE login so we can migrate its data.
	const prev = state.session
	const anonToken = prev?.user?.is_anonymous ? prev.access_token : null

	const rawNonce = randomHex()
	const nonceDigest = await sha256Hex(rawNonce)

	const SocialLogin = cap()?.Plugins?.SocialLogin
	if (!SocialLogin) throw new Error('SocialLogin plugin unavailable')
	// Android is configured by the web client id (Credential Manager); iOS by
	// its own iOS client id (+ reversed-id URL scheme in Info.plist).
	await SocialLogin.initialize({
		google: isIOS()
			? { iOSClientId: GOOGLE_IOS_CLIENT_ID, mode: 'online' }
			: { webClientId: GOOGLE_WEB_CLIENT_ID, mode: 'online' },
	})
	// No `scopes` here on purpose: we only need authentication (an id token for
	// Supabase), and the basic Credential Manager flow already returns email/name.
	// Passing scopes triggers the plugin's OAuth-scopes path, which requires native
	// MainActivity changes and otherwise throws "You CANNOT use scopes…".
	let res
	try {
		res = await SocialLogin.login({
			provider: 'google',
			options: { nonce: nonceDigest },
		})
	} catch (e: any) {
		if (isUserCancelled(e)) return null // dismissed the sheet — not an error
		throw e
	}
	const idToken = res?.result?.idToken
	if (!idToken) throw new Error('Google sign-in returned no id token')

	let { data, error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken, nonce: rawNonce })
	if (error && /nonce/i.test(error.message)) {
		// The iOS Google SDK may not embed our nonce in the id token — retry
		// without it (Supabase then skips the nonce comparison).
		;({ data, error } = await supabase.auth.signInWithIdToken({ provider: 'google', token: idToken }))
	}
	if (error) throw error
	updateState(data.session)
	emitter.emit('auth:change', { user: data.session?.user, isAuthenticated: true })

	// Migrate the guest's data, then reboot so the UI reflects the signed-in state.
	if (anonToken && data.session) await claimAnonymousData(data.session.access_token, anonToken)
	window.location.href = '/'
	return data
}

// --- Native Apple sign-in (iOS Sign in with Apple → identity token → Supabase) ---
// Same shape as the Google native flow: the provider gets the hashed nonce, Supabase
// gets the raw one and compares. iOS only — the plugin's Apple flow needs no config
// (it uses the app's "Sign in with Apple" capability + bundle id as the audience).
// The SocialLogin plugin rejects with this code when the user dismisses the
// system sheet — a normal action, not a failure to surface in the UI.
const isUserCancelled = (e: any) => e?.code === 'USER_CANCELLED'

async function signInWithAppleNative() {
	const prev = state.session
	const anonToken = prev?.user?.is_anonymous ? prev.access_token : null

	const rawNonce = randomHex()
	const nonceDigest = await sha256Hex(rawNonce)

	const SocialLogin = cap()?.Plugins?.SocialLogin
	if (!SocialLogin) throw new Error('SocialLogin plugin unavailable')
	await SocialLogin.initialize({ apple: {} })
	const loginOnce = () => SocialLogin.login({
		provider: 'apple',
		options: { scopes: ['name', 'email'], nonce: nonceDigest },
	})
	// iOS flake: the very first ASAuthorization request after a fresh install can
	// fail with AuthenticationServices error 1000 (AuthKit cold start) and succeed
	// on the next attempt — App Review hit exactly this (rejection 2026-07-10).
	// Retry once before surfacing an error; never retry or surface a user cancel.
	let res
	try {
		res = await loginOnce()
	} catch (e: any) {
		if (isUserCancelled(e)) return null
		await new Promise(r => setTimeout(r, 400))
		try {
			res = await loginOnce()
		} catch (e2: any) {
			if (isUserCancelled(e2)) return null
			throw e2
		}
	}
	const idToken = res?.result?.idToken
	if (!idToken) throw new Error('Apple sign-in returned no identity token')

	const { data, error } = await supabase.auth.signInWithIdToken({ provider: 'apple', token: idToken, nonce: rawNonce })
	if (error) throw error
	updateState(data.session)
	emitter.emit('auth:change', { user: data.session?.user, isAuthenticated: true })

	if (anonToken && data.session) await claimAnonymousData(data.session.access_token, anonToken)
	window.location.href = '/'
	return data
}

const auth = {
	initialize: async () => {
		if (state.initialized) {
			console.warn('Auth already initialized')
			return state.session
		}

		try {
			// First, try to get the session from storage
			const { data: { session }, error } = await supabase.auth.getSession()

			if (error) {
				console.error('Error getting session:', error)
			}

			if (session) {
				console.log('Found existing session')
				updateState(session)
				emitter.emit('auth:change', { user: session.user, isAuthenticated: true })
			}
			// No anonymous sign-in at boot. It's deferred to the first server
			// write (see ensureSession) so we don't mint an anonymous user for
			// visitors who open the app and leave without creating anything.

			// Set up auth state change listener
			const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
				console.log('Auth event:', event)

				// Handle different auth events
				switch (event) {
					case 'SIGNED_IN':
						console.log('User signed in successfully')
						updateState(session)
						if (session) {
							emitter.emit('auth:change', { user: session.user, isAuthenticated: true })
						}
						break
					case 'SIGNED_OUT':
						console.log('User signed out')
						updateState(null)
						emitter.emit('auth:change', { user: null, isAuthenticated: false })
						break
					case 'TOKEN_REFRESHED':
						console.log('Token refreshed')
						updateState(session)
						break
					case 'USER_UPDATED':
						console.log('User updated')
						updateState(session)
						break
					default:
						updateState(session)
				}
			})

			// On native, finish OAuth when the Custom Tab redirects back via the
			// deep link: pull the code out of the URL and exchange it for a session,
			// then reboot the SPA so the UI reflects the signed-in state.
			if (isNative()) {
				cap().Plugins.App.addListener('appUrlOpen', async ({ url }: { url: string }) => {
					if (!url || !url.startsWith('dev.codesuma.papalist://')) return
					try { await cap().Plugins.Browser.close() } catch { /* tab may already be closed */ }
					try {
						const query = url.includes('?') ? url.split('?')[1] : ''
						const code = new URLSearchParams(query).get('code')
						if (!code) return
						// Capture the anonymous session before the exchange replaces it,
						// so the guest's lists migrate to the new account (the native
						// sheet flows do the same via claimAnonymousData).
						const prev = state.session
						const anonToken = prev?.user?.is_anonymous ? prev.access_token : null
						const { data, error: exErr } = await supabase.auth.exchangeCodeForSession(code)
						if (exErr) { console.error('exchangeCodeForSession failed:', exErr); return }
						if (anonToken && data.session) await claimAnonymousData(data.session.access_token, anonToken)
						window.location.href = '/'
					} catch (e) {
						console.error('OAuth deep link handling failed:', e)
					}
				})
			}

			state.initialized = true
			notifySubscribers()

			// Check localStorage to debug
			const storedSession = localStorage.getItem('sb-xeclrrhsnzwxjvjnaive-auth-token')
			console.log('Stored session in localStorage:', storedSession ? 'Present' : 'Not found')

			return session
		} catch (error) {
			console.error('Failed to initialize auth:', error)
			state.initialized = true
			throw error
		}
	},

	// Lazily create an anonymous session the first time the app needs the server
	// (first list created / first item added). Deferring it from boot means we
	// don't mint an anonymous user for visitors who never create anything.
	// Idempotent: returns the existing session if any, and de-dupes concurrent calls.
	ensureSession: async (): Promise<Session | null> => {
		await auth.waitForInitialization()
		if (state.session) return state.session
		if (!ensuring) {
			ensuring = (async () => {
				const { data, error } = await supabase.auth.signInAnonymously()
				if (error) {
					ensuring = null
					console.error('Anonymous sign-in failed:', error)
					throw error
				}
				updateState(data.session)
				emitter.emit('auth:change', { user: data.session?.user, isAuthenticated: true })
				return data.session
			})()
		}
		return ensuring
	},

	subscribe: (callback: (state: any) => void) => {
		subscribers.add(callback)
		// Immediately call with current state
		callback({
			token: state.token,
			user: state.user,
			isAuthenticated: !!state.token,
			initialized: state.initialized
		})
		return () => subscribers.delete(callback)
	},

	getToken: () => state.token,
	getUser: () => state.user,
	getSession: () => state.session,
	isAuthenticated: () => !!state.token,

	signInWithGoogle: async () => {
		// Native: the system Google account sheet (no browser, no URL). Android is
		// configured by the web client id, iOS by GOOGLE_IOS_CLIENT_ID (the missing
		// iOS config was the cause of the Guideline 2.1(a) rejection 2026-07-10 and
		// why iOS used the browser flow until 1.2.2). The browser flow below stays
		// as the fallback; it needs dev.codesuma.papalist://login-callback in
		// Supabase Auth → Redirect URLs.
		if (isNative() && (isIOS() ? GOOGLE_IOS_CLIENT_ID : GOOGLE_WEB_CLIENT_ID)) return signInWithGoogleNative()

		try {
			const native = isNative()
			// Native (fallback): redirect to the app's custom scheme (deep link) so the
			// result returns to the app. Web: back to the current page (detectSessionInUrl).
			const redirectTo = native ? OAUTH_REDIRECT : `${window.location.origin}${window.location.pathname}`

			const { data, error } = await supabase.auth.signInWithOAuth({
				provider: 'google',
				options: {
					redirectTo,
					skipBrowserRedirect: native, // we open the Custom Tab ourselves on native
					queryParams: {
						access_type: 'offline',
						prompt: 'consent'
					}
				}
			})

			if (error) {
				console.error('Google sign-in error:', error)
				throw error
			}

			// Google blocks OAuth inside a WebView, so on native open the URL in a
			// Custom Tab; the deep link brings the result back (see initialize()).
			if (native && data?.url) await cap().Plugins.Browser.open({ url: data.url })

			console.log('OAuth sign-in initiated:', data)
			return data
		} catch (error) {
			console.error('Failed to sign in with Google:', error)
			throw error
		}
	},

	signInWithApple: async () => {
		// Native iOS: the system Sign in with Apple sheet → identity token → Supabase.
		if (isIOS()) return signInWithAppleNative()

		// Web/other: standard OAuth redirect. Requires Apple's web OAuth (Services ID +
		// secret) to be configured in the Supabase Apple provider.
		const { data, error } = await supabase.auth.signInWithOAuth({
			provider: 'apple',
			options: { redirectTo: `${window.location.origin}${window.location.pathname}` }
		})
		if (error) {
			console.error('Apple sign-in error:', error)
			throw error
		}
		return data
	},

	signOut: async () => {
		try {
			const { error } = await supabase.auth.signOut()
			if (error) {
				console.error('Sign out error:', error)
				throw error
			}
			updateState(null)
		} catch (error) {
			console.error('Failed to sign out:', error)
			throw error
		}
	},

	refreshSession: async () => {
		try {
			const { data: { session }, error } = await supabase.auth.refreshSession()
			if (error) {
				console.error('Refresh session error:', error)
				throw error
			}
			updateState(session)
			return session
		} catch (error) {
			console.error('Failed to refresh session:', error)
			throw error
		}
	},

	// Helper method to manually check for session
	checkSession: async () => {
		const { data: { session }, error } = await supabase.auth.getSession()
		if (error) {
			console.error('Check session error:', error)
			return null
		}
		updateState(session)
		return session
	},

	isInitialized: () => state.initialized,

	waitForInitialization: async () => {
		if (state.initialized) return state
		return new Promise<AuthState>(resolve => {
			const unsub = auth.subscribe((state: any) => {
				if (state.initialized) {
					unsub()
					resolve(state)
				}
			})
		})
	}
}

export default {
	auth
}
